import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  IconButton,
  CircularProgress,
  Chip
} from '@mui/material';
import { 
  AutoAwesome, 
  Analytics,
  Assignment,
  Logout,
  Storage,
  Slideshow,
  Folder
} from '@mui/icons-material';

import helmTheme from './theme/helmTheme';
import { auth } from './firebase';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { API_URL } from './config';

// Import page components
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DatasetsPage from './pages/DatasetsPage';
import PlanPage from './pages/PlanPage';
import PresentPage from './pages/PresentPage';
// import AdvisorPage from './pages/AdvisorPage'; // Hidden for now

function App() {
  const [currentTab, setCurrentTab] = useState(0);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeCorpus, setActiveCorpus] = useState(null);
  
  // Load active corpus info on mount
  useEffect(() => {
    const loadCorpusInfo = async () => {
      let corpusId = localStorage.getItem('corpus_id');
      
      // Default to Skipper Demo if no corpus set
      if (!corpusId) {
        corpusId = 'demo-skipper-corpus-2024';
        localStorage.setItem('corpus_id', corpusId);
        console.log('✅ Defaulting to Skipper Demo corpus');
      }
      
      // Determine corpus name
      if (corpusId === 'demo-skipper-corpus-2024') {
        setActiveCorpus({ id: corpusId, name: 'Skipper Demo', type: 'demo' });
      } else {
        setActiveCorpus({ id: corpusId, name: 'My Data', type: 'personal' });
      }
    };
    
    if (isAuthenticated) {
      loadCorpusInfo();
    }
  }, [isAuthenticated]);

  // Proactively refresh Firebase token every 50 minutes to prevent expiration
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const refreshToken = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          await user.getIdToken(true); // Force refresh
          console.log('✅ Token refreshed proactively');
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
      }
    };
    
    // Refresh immediately on mount
    refreshToken();
    
    // Then refresh every 50 minutes (tokens expire after 60 minutes)
    const interval = setInterval(refreshToken, 50 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Check for existing Firebase auth session on mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in - restore session
        console.log('Existing Firebase session found:', firebaseUser.email);
        
        // Check if email is whitelisted by calling backend
        try {
          const idToken = await firebaseUser.getIdToken();
          const existingCorpusId = localStorage.getItem('corpus_id');
          
          const response = await fetch(`${API_URL}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              idToken,
              email: firebaseUser.email,
              uid: firebaseUser.uid,
              corpus_id: existingCorpusId
            })
          });
          
          if (response.ok) {
            const loginResult = await response.json();
            
            // Store corpus_id
            if (loginResult.corpus_id) {
              localStorage.setItem('corpus_id', loginResult.corpus_id);
            }
            
            // Extract name from email
            const name = firebaseUser.email.split('@')[0].split('.').map(
              word => word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
            
            // Restore user state
            setUser({
              name,
              email: firebaseUser.email,
              company: 'Skipper inc.',
              role: 'Analyst',
              uid: firebaseUser.uid
            });
            setIsAuthenticated(true);
            console.log('✅ Session restored');
          } else {
            // Backend rejected - sign out
            await signOut(auth);
          }
        } catch (error) {
          console.error('Session restore error:', error);
          await signOut(auth);
        }
      }
      
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      // Sign out from Firebase
      await signOut(auth);
      
      // Clear backend session
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      
      // Keep corpus_id in localStorage so data persists across logins
      // Uncomment to clear data on logout (fresh start):
      // localStorage.removeItem('corpus_id');
      
      // Clear local state
      setUser(null);
      setIsAuthenticated(false);
      setCurrentTab(0);
      
      console.log('Logged out successfully (data preserved for next login)');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if backend call fails
      setUser(null);
      setIsAuthenticated(false);
      setCurrentTab(0);
    }
  };

  const tabs = [
    { label: 'Datasets', icon: <Storage />, component: <DatasetsPage /> },
    { label: 'Plan', icon: <AutoAwesome />, component: <PlanPage /> },
    { label: 'Present', icon: <Slideshow />, component: <PresentPage /> }
    // { label: 'Advisor', icon: <AutoAwesome />, component: <AdvisorPage /> } // Hidden
  ];

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <ThemeProvider theme={helmTheme}>
        <CssBaseline />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  // Show login or register page if not authenticated
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={helmTheme}>
        <CssBaseline />
        {showRegister ? (
          <RegisterPage 
            onRegisterSuccess={handleLogin}
            onBackToLogin={() => setShowRegister(false)}
          />
        ) : (
          <LoginPage 
            onLogin={handleLogin}
            onShowRegister={() => setShowRegister(true)}
          />
        )}
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={helmTheme}>
      <CssBaseline />
      <Box sx={{ backgroundColor: '#FAFAFA', minHeight: '100vh' }}>
        
        {/* Header */}
        <AppBar 
          position="static" 
          elevation={0}
          sx={{ 
            background: 'linear-gradient(90deg, #F8F9FA 0%, #FFFFFF 100%)',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: '#E1E5E9'
          }}
        >
          <Toolbar sx={{ py: 1, minHeight: 64 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexGrow: 1 }}>
              {/* Logo */}
              <img 
                src="/assets/helm_logo.avif" 
                alt="Helm.ai" 
                style={{ height: 28 }}
              />
              
              {/* Active Corpus Indicator */}
              {activeCorpus && (
                <Chip
                  icon={<Folder sx={{ fontSize: 16 }} />}
                  label={activeCorpus.name}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    backgroundColor: 'rgba(71, 189, 198, 0.08)',
                    '& .MuiChip-icon': {
                      color: 'primary.main'
                    }
                  }}
                />
              )}
              
              {/* Navigation Tabs - Level with Logo */}
              <Tabs 
                value={currentTab} 
                onChange={handleTabChange}
                sx={{ 
                  '& .MuiTabs-flexContainer': {
                    gap: 2
                  }
                }}
              >
                {tabs.map((tab, index) => (
                  <Tab 
                    key={index}
                    icon={tab.icon}
                    label={tab.label}
                    iconPosition="start"
                    sx={{ 
                      minHeight: 48,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      minWidth: 'auto',
                      px: 2
                    }}
                  />
                ))}
              </Tabs>
            </Box>
            
            {/* User Profile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {user?.name || 'Luke Wilson-Mawer'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {user?.company || 'Skipper inc.'}
                </Typography>
              </Box>
              <Box 
                sx={{ 
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #47BDC6 0%, #845BA3 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              >
                {user?.name?.split(' ').map(n => n[0]).join('') || 'LW'}
              </Box>
              <IconButton 
                onClick={handleLogout}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { 
                    color: 'error.main',
                    backgroundColor: 'error.light'
                  }
                }}
                title="Logout"
              >
                <Logout />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box sx={{ 
          p: currentTab === 2 ? 0 : 4,
          height: currentTab === 2 ? 'calc(100vh - 64px)' : 'auto'
        }}>
          {tabs[currentTab].component}
        </Box>

        {/* Footer - Hidden on Plan page for full height */}
        {currentTab !== 2 && (
          <Box 
            component="footer" 
            sx={{ 
              mt: 4, 
              py: 2, 
              backgroundColor: 'background.paper',
              borderTop: 1,
              borderColor: 'divider'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Helm.ai Marketing Intelligence • Real Data Analysis Platform
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default App;